//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ContentLibraryExportCollectionRequest,
  ContentLibrarySettingsRequest,
  ContentLibrarySettingsResponse,
  IContentLibraryAggregationRequest,
  IContentLibraryAggregationResponse,
  IContentLibraryAutocompleteRequest,
  IContentLibraryAutocompleteResponse,
  IContentLibraryItemParams,
  IContentLibraryItemsDataResponse,
  IContentLibraryItemsResponse,
  IContentLibraryProcessingResponse,
  IContentLibraryUploadRequest,
} from '@apis/content-library/contentLibraryApiModel';
import { deserializeAdMetadata } from '@apis/content-library/contentLibraryApiUtils';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { sessionXhr } from '@utils/sessionXhr';

import { IContentItemDetails } from 'app/bulk-actions/model/AppState';

const CONTENT_LIBRARY_BASE_URL = 'api/content-library';

function contentLibraryFetch(endpoint: string, init?: RequestInit) {
  return serializedSessionFetch(`${CONTENT_LIBRARY_BASE_URL}${endpoint}`, init);
}

/**
 * Upload new content library media item
 * (track progress using `uploadProgress` callback)
 *
 * POST /api/v1/media/library/add-by-bytes
 */
export const uploadContentLibraryItem = async ({
  collectionId,
  file,
  init,
  uploadProgress,
}: IContentLibraryUploadRequest): Promise<XMLHttpRequest> => {
  const body = new FormData();
  body.append('file', file);

  const collectionIdParam = collectionId ? `?collectionId=${collectionId}` : '';

  return await sessionXhr(
    'web_gateway',
    `/api/v1/media/library/add-by-bytes${collectionIdParam}`,
    uploadProgress,
    {
      body,
      method: 'POST',
      headers: { 'Upload-Length': `${file.size}` },
      ...init,
    },
  );
};

/**
 * Retrieve identifiers for items currently being processed.
 * (Check the video processing status: if the backend is still processing the item, its ID will appear in the result list.)
 *
 * GET /api/v1/videos/processing
 */
export const getContentLibraryProcessingItems = async (
  init?: RequestInit,
): Promise<string[]> => {
  const res = await serializedGatewayFetch(
    'web_gateway',
    '/api/v1/videos/processing',
    {
      ...(init ?? {}),
    },
  );
  const payload = await res.json<IContentLibraryProcessingResponse>();

  return payload.savedMediaIds;
};

export const getContentLibraryItems = async (
  params: IContentLibraryItemParams,
  init?: RequestInit,
): Promise<IContentLibraryItemsResponse> => {
  const res = await serializedGatewayFetch(
    'web_gateway',
    '/api/v1/items/search',
    {
      ...(init ? init : {}),
      method: 'POST',
      body: JSON.stringify(params),
    },
  );

  const payload = await res.json<IContentLibraryItemsDataResponse>((res) => ({
    data: {
      ...res.data,
      contentItems: res.data.contentItems.map((item) => ({
        ...item,
        adMetadata: isDefined(item.adMetadata)
          ? deserializeAdMetadata(item.adMetadata)
          : undefined,
      })),
    },
  }));

  return payload.data;
};

export const getContentLibraryAutocomplete = async (
  req: IContentLibraryAutocompleteRequest,
  init?: RequestInit,
): Promise<IContentLibraryAutocompleteResponse> => {
  const res = await contentLibraryFetch('/autocomplete', {
    ...(init ? init : {}),
    method: 'POST',
    body: JSON.stringify(req),
  });
  return res.json();
};

export const getItemDetails = async (
  itemIds: (Cuid | PartialCuid)[],
): Promise<Record<string, IContentItemDetails>> => {
  const params = makeParams({ itemIds });
  const res = await serializedGatewayFetch(
    'web_gateway',
    `/api/v1/item-details?${params}`,
  );
  return res.json();
};

/**
 * Get content library aggregation result
 *
 * POST /api/content-library/aggregate
 */
export const getContentLibraryAggregation = async (
  req: IContentLibraryAggregationRequest,
  init?: RequestInit,
): Promise<IContentLibraryAggregationResponse> => {
  const res = await contentLibraryFetch('/aggregate', {
    ...(init ? init : {}),
    method: 'POST',
    body: JSON.stringify(req),
  });
  return res.json();
};

/**
 *
 * GET /api/content-library/settings
 *
 * @returns content library settings, including user ids selection and whether we should show the collection tooltip.
 */
export const getContentLibrarySettings = async (
  init?: RequestInit,
): Promise<ContentLibrarySettingsResponse> => {
  const res = await contentLibraryFetch('/settings', {
    ...init,
    method: 'GET',
  });
  return res.json<ContentLibrarySettingsResponse>();
};

/**
 * PUT /api/content-library/settings
 */
export const updateContentLibrarySettings = async (
  req: ContentLibrarySettingsRequest,
  init?: RequestInit,
): Promise<ContentLibrarySettingsResponse> => {
  const res = await contentLibraryFetch('/settings', {
    ...init,
    method: 'PUT',
    body: JSON.stringify(req),
  });
  return res.json<ContentLibrarySettingsResponse>();
};

/**
 * Gets a ZIP file containing the collection summary metrics
 *
 * POST /api/content-library/export-collection/:collectionId/zip
 */
export const getExportCollectionMetrics = async (
  { collectionId, ...body }: ContentLibraryExportCollectionRequest,
  init?: RequestInit,
): Promise<Blob | undefined> => {
  const res = await contentLibraryFetch(
    `/export-collection/${collectionId}/zip`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  return res.blob();
};

/**
 * Gets a csv that contains media items
 *
 * POST /api/v1/items/export-csv
 */
export const exportClientMediaCsv = async (
  params: IContentLibraryItemParams,
  init?: RequestInit,
): Promise<Blob> => {
  const res = await serializedGatewayFetch(
    'web_gateway',
    `/api/v1/items/export-csv`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  return res.blob();
};
